import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { presetsApi } from "../../api/presets";
import { downloadKit } from "../../lib/kitDownload";
import { useAuth } from "../../hooks/useAuth";
import PluginWindow, { PALETTE_MODES } from "./PluginWindow";
import PreviewKnob from "./PreviewKnob";
import { kitWaveColor } from "../../lib/kitColor";
import { useLoopPlayer } from "../../lib/useLoopPlayer";
import RatingStars from "./RatingStars";
import FavoriteButton from "./FavoriteButton";
import CopyLinkButton from "./CopyLinkButton";

// The plugin's three lane palettes take turns: each new kit opened moves on
// to the next one (red to green, green to blue, purple to red, and round), so
// browsing kits shows off all three. A kit keeps its palette for the rest of
// the visit (and React calling this twice for one kit costs no turn); the
// turn itself is remembered per browser, and when storage is unavailable
// (private mode) the page simply stays on the classic palette.
const paletteByKit = new Map();
function paletteFor(id) {
  if (!paletteByKit.has(id)) {
    let turn = 0;
    try {
      turn = parseInt(localStorage.getItem("tm-kit-palette-turn") || "0", 10) || 0;
      localStorage.setItem("tm-kit-palette-turn", String(turn + 1));
    } catch {
      turn = 0;
    }
    paletteByKit.set(id, PALETTE_MODES[turn % PALETTE_MODES.length]);
  }
  return paletteByKit.get(id);
}

export default function KitDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);

  // A client-side navigation keeps the window's scroll offset, so opening a
  // kit from a scrolled catalogue landed at the bottom of this (shorter) page.
  useEffect(() => { window.scrollTo(0, 0); }, [id]);
  const [error, setError] = useState("");
  const [reported, setReported] = useState(false);

  // Playback lives entirely in the Web Audio graph (see useLoopPlayer): an
  // AudioBufferSourceNode with loop=true schedules the wrap INSIDE the
  // audio graph, which is sample-accurate. That replaces both an <audio>
  // element's native `loop` (which restarts at the file's duration) and a
  // hand-rolled currentTime wrap - either way, looping on a media element's
  // clock could never fully close the gap, which is what made the loop
  // restart audible before this fix.
  //
  // No fallback: an item with no embedded preview audio (a kit shared before
  // this change, or exported by a plugin build that predates it) passes
  // `null` here instead of a URL. `item` is `null` itself while the fetch
  // below is still in flight, so `hasPreviewAudio` starts false and flips to
  // whatever the server actually reported once it resolves - never a real
  // URL for an item that turns out to have none.
  const palette = useMemo(() => paletteFor(id), [id]);

  const hasPreviewAudio = Boolean(item?.has_preview_audio);
  const { isPlaying, isLoading, error: playerError, toggle, getPosition, getDuration } =
    useLoopPlayer(hasPreviewAudio ? presetsApi.previewUrl(id) : null);

  // Spacebar toggles play/pause anywhere on this page, EXCEPT: while typing
  // (an input/textarea/select/contenteditable target), and while a button or
  // link has focus. The second guard matters specifically for the play/pause
  // button itself - the browser already activates a focused button on Space
  // (keydown here, then a synthesized click on keyup), so without it a
  // single space press would toggle twice and net out to nothing. This
  // listener is detail-view only; the catalogue does not get a spacebar
  // shortcut.
  //
  // With no preview audio, this must do nothing at all, not error: there is
  // no dead play button anywhere on this page to toggle, so the handler
  // bails out before ever calling toggle().
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!hasPreviewAudio) return;
      if (e.key !== " " && e.code !== "Space") return;
      const target = e.target;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable) {
        return;
      }
      if (tag === "BUTTON" || tag === "A") return;
      e.preventDefault();
      toggle();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [hasPreviewAudio, toggle]);

  useEffect(() => {
    let alive = true;
    // Reset per-item state on every id change: without this, navigating from
    // one kit's page straight to another (component stays mounted, only the
    // :id param changes) would flash the previous kit's title/error/reported
    // state for a frame before the new fetch resolves.
    setItem(null);
    setError("");
    setReported(false);
    presetsApi
      .get(id)
      .then((r) => {
        if (alive) setItem(r?.data || false);
      })
      .catch(() => {
        if (alive) setItem(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  if (item === false) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center gap-4">
        <p className="text-[#A1A1AA]">Not found.</p>
        <Link
          to="/kits"
          className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] hover:text-[#FF5500]"
        >
          ← Back to kits
        </Link>
      </div>
    );
  }
  if (!item) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <p className="text-[#A1A1AA]">Loading...</p>
      </div>
    );
  }

  const onDownload = async () => {
    if (!user) { navigate("/login"); return; }
    setBusy(true);
    setError("");
    try {
      await downloadKit(item);
    } catch (err) {
      setError(err?.response?.data?.detail || "Download failed.");
    } finally {
      setBusy(false);
    }
  };

  const onReport = async () => {
    if (!user) { navigate("/login"); return; }
    setReportBusy(true);
    setError("");
    try {
      await presetsApi.report(id);
      setReported(true);
    } catch (err) {
      // Mirror onDownload: a rate-limited or already-flagged report must not
      // look identical to success, so surface the server's own detail text.
      setError(err?.response?.data?.detail || "Report failed.");
    } finally {
      setReportBusy(false);
    }
  };

  // RatingStars / FavoriteButton hand back the server's own authoritative
  // numbers after every successful action (never on failure -- both
  // components roll their own optimistic guess back locally instead), so
  // merging that into `item` keeps the displayed average/count/favourite
  // total truthful without a full refetch of the kit.
  const onRatingChange = (data) => setItem((prev) => (prev ? { ...prev, ...data } : prev));
  const onFavoriteChange = (data) => setItem((prev) => (prev ? { ...prev, ...data } : prev));

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="mx-auto max-w-6xl px-6 py-5 sm:py-7">
        <Link
          to="/kits"
          className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] hover:text-[#FF5500]"
        >
          ← Back to kits
        </Link>

        {/* One header row so the whole page fits on screen at load: what the
            kit is on the left (title, details, rating, favourite), what you
            do with it on the right (play, download, report). */}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            {/* The favourite sits on the title line: beside the rating it
                would hang below the stars, whose status line reserves room
                underneath them. */}
            <div className="flex items-center gap-3">
              <h1 className="min-w-0 truncate font-clash text-3xl font-bold sm:text-4xl">{item.title}</h1>
              <FavoriteButton
                presetId={id}
                isFavorited={item.is_favorited}
                favoritesCount={item.favorites_count}
                size="md"
                onChange={onFavoriteChange}
              />
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-2">
              <span className="text-[#A1A1AA]">
                {item.genre} &middot; {Math.round(item.bpm || 0)} BPM &middot; {item.downloads ?? 0}{" "}
                {item.downloads === 1 ? "download" : "downloads"}
              </span>
              <RatingStars
                presetId={id}
                ratingAvg={item.rating_avg}
                ratingCount={item.rating_count}
                myRating={item.my_rating}
                interactive
                size="md"
                onRatingChange={onRatingChange}
              />
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            {hasPreviewAudio ? (
              <PreviewKnob
                state={isLoading ? "loading" : isPlaying ? "playing" : "idle"}
                color={kitWaveColor(id)}
                label={isPlaying ? "Pause preview" : "Play preview"}
                title="Loops automatically"
                onClick={toggle}
                getPosition={getPosition}
                getDuration={getDuration}
              />
            ) : (
              // Finished state, not a failure: this kit was shared without an
              // embedded preview (an older plugin build) - deliberately no
              // fallback render, so no play button, just honest copy.
              <span className="text-sm text-[#A1A1AA]">No preview audio</span>
            )}
            <button
              className="btn-primary rounded px-6 py-3 text-sm font-semibold disabled:opacity-50"
              onClick={onDownload}
              disabled={busy}
            >
              {user ? (busy ? "Preparing..." : "Download") : "Log in to download"}
            </button>
            <CopyLinkButton item={{ ...item, id }} variant="button" />
            <button
              className="rounded border border-white/10 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-white/5 disabled:opacity-50"
              onClick={onReport}
              disabled={reported || reportBusy}
            >
              {reported ? "Reported" : reportBusy ? "Reporting..." : "Report"}
            </button>
          </div>
        </div>
        {item.description && <p className="mt-2 line-clamp-2 text-sm text-white/80">{item.description}</p>}

        {(error || playerError) && (
          <div className="mt-3 rounded border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-400">
            {error || playerError}
          </div>
        )}

        {/* No <audio> element at all: useLoopPlayer fetches the Ogg file
            once, decodes it into an AudioBuffer, and plays it through an
            AudioBufferSourceNode with loop=true - see the hook for why that
            is what makes the loop gapless. */}
        <div className="mt-4">
          <PluginWindow grid={item.grid} getPosition={getPosition} isPlaying={isPlaying} fitToViewport palette={palette} />
        </div>
      </div>
    </div>
  );
}
